// The Table's sort comparator, kept out of Table.jsx so it can be unit-tested — this repo has no
// rendering harness, so a comparator left inline in the component could only ever be checked by hand.
//
// Three ways a row's sort value is resolved, in this precedence:
//   1. `customSortFunction` — a table-wide override held in Table's own state.
//   2. `sortValue(row)` — declared on the active column's config, for a column whose sortable value
//      isn't a plain field on the row (an object, or a value that needs normalising before it
//      compares correctly — e.g. zero-padding a version name so "1.9" sorts before "1.10").
//   3. `getAttr(row, sortKey)` — the row field named by the column's `key`. The default for every
//      column, and the fallback whenever the active column declares no `sortValue`.

// Read `key` (a dot-path) off `obj`, or "" when any step along the way is missing. That "" is what
// makes an absent value sort last — see compareRows's empty handling.
export function getAttr(obj, key) {
  let keys = key.split(".");
  let val = obj;
  for (let k of keys) {
    if (val) val = val[k];
    if (val === undefined) return "";
  }

  return val;
}

// Compare two rows for the active sort. `sortValue` is the active column's accessor, already
// resolved by the caller (Table looks the column up once per sort, not once per comparison).
//
// The direction handling below is deliberately left exactly as it has always been: for "desc" the
// string branch returns aVal.localeCompare(bVal), i.e. ascending, and the numeric branch is inverted
// the same way, so the header arrow contradicts the actual order. Every consumer's tables (DOS, BOS,
// MOS, SOS) have sorted this way for their whole life; correcting it here would silently flip the
// order of all of them at once, so it is a separate decision from adding `sortValue`.
export function compareRows(
  a,
  b,
  { sortKey, sortDirection, customSortFunction, sortValue } = {},
) {
  if (!sortDirection || !sortKey) return 0;
  let aVal, bVal;

  if (customSortFunction) {
    aVal = customSortFunction(a);
    bVal = customSortFunction(b);
  } else if (sortValue) {
    aVal = sortValue(a);
    bVal = sortValue(b);
  } else {
    aVal = getAttr(a, sortKey);
    bVal = getAttr(b, sortKey);
  }

  // Empty values (including the "" getAttr/sortValue return for "nothing here") always go last,
  // whichever direction the sort is in.
  if (!aVal && !bVal) {
    return 0;
  }
  if (!aVal) {
    return 1;
  }
  if (!bVal) {
    return -1;
  }
  if (typeof aVal === "string" && typeof bVal === "string") {
    return sortDirection === "desc"
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  }

  if (aVal < bVal) return sortDirection === "asc" ? 1 : -1;
  if (aVal > bVal) return sortDirection === "asc" ? -1 : 1;
  return 0;
}
